import { useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const TESTIMONIALS = [
  {
    quote: 'Tony and his crew replaced our entire furnace system in under a day. From the moment they arrived to the final cleanup, everything was flawless. They left the basement cleaner than it was before they started.',
    name: 'Michael R.',
    location: 'Oakville',
    rating: 5,
    platform: 'HomeStars',
    initials: 'MR',
  },
  {
    quote: 'Called for an AC breakdown on a July afternoon. Steve arrived within a few hours, diagnosed the issue immediately, and had us running again the same day. Honest pricing, zero surprises on the invoice.',
    name: 'Jennifer T.',
    location: 'Burlington',
    rating: 5,
    platform: 'HomeStars',
    initials: 'JT',
  },
  {
    quote: 'After getting quotes from three HVAC companies, Atlantis offered the most transparent pricing and best advice. No pressure, no upselling — just honest answers. The installation was seamless.',
    name: 'David K.',
    location: 'Mississauga',
    rating: 5,
    platform: 'Google',
    initials: 'DK',
  },
  {
    quote: 'I appreciate that they never try to oversell. They assessed our old furnace and gave us a genuinely honest recommendation. The new system has been running perfectly. Will absolutely call Atlantis again.',
    name: 'Robert L.',
    location: 'Etobicoke',
    rating: 5,
    platform: 'HomeStars',
    initials: 'RL',
  },
  {
    quote: 'The HRV installation was handled professionally from start to finish. The team explained everything clearly, answered all my questions, and the work area was spotless when they left. Exceptional service.',
    name: 'Sarah M.',
    location: 'Toronto',
    rating: 5,
    platform: 'Google',
    initials: 'SM',
  },
  {
    quote: 'Responsive, professional, and incredibly thorough. Our new AC system was installed perfectly and on time. The technician walked us through the system operation before he left. Outstanding experience.',
    name: 'Patricia W.',
    location: 'Burlington',
    rating: 5,
    platform: 'HomeStars',
    initials: 'PW',
  },
];

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const featured = TESTIMONIALS[active];

  return (
    <section
      ref={ref}
      className="relative py-28 lg:py-36 overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #030D1F 0%, #071224 60%, #030D1F 100%)' }}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 70% at 80% 30%, rgba(26,101,224,0.08) 0%, transparent 65%)' }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(26,101,224,0.28), transparent)' }} />

      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-14">

        {/* Header */}
        <div
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-0.5 bg-luxury-gold" />
              <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-white">Client Reviews</span>
            </div>
            <h2 className="font-serif text-5xl lg:text-7xl text-white leading-[1.0]">
              What Our Clients<br />
              <em className="not-italic text-luxury-gold">Are Saying</em>
            </h2>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-luxury-gold text-lg" />)}
            </div>
            <span className="font-caps text-[10px] tracking-[0.2em] uppercase text-white">4.9 Average · HomeStars Verified</span>
          </div>
        </div>

        {/* Featured Testimonial */}
        <div
          className={`relative rounded-3xl overflow-hidden mb-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/6837094835821b7c05233e913d11edab.jpg"
              alt=""
              className="w-full h-full object-cover object-center opacity-10"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(3,13,31,0.97) 40%, rgba(7,18,36,0.93) 100%)' }} />
          </div>
          <div className="absolute top-0 right-0 w-80 h-80 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(26,101,224,0.10) 0%, transparent 70%)' }} />

          <div className="relative p-10 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Avatar */}
              <div className="lg:col-span-1">
                <div className="w-16 h-16 flex items-center justify-center rounded-full border border-luxury-gold/35 bg-luxury-gold/12">
                  <span className="font-serif text-luxury-gold text-2xl font-bold">{featured.initials}</span>
                </div>
              </div>

              {/* Quote */}
              <div className="lg:col-span-11">
                <div className="font-serif text-luxury-gold/15 text-8xl leading-none mb-0 select-none">&ldquo;</div>
                <blockquote className="font-serif text-white text-2xl lg:text-4xl leading-relaxed italic font-light -mt-6 mb-8">
                  {featured.quote}
                </blockquote>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="font-caps font-bold text-sm text-white tracking-[0.15em] whitespace-nowrap">{featured.name}</div>
                    <div className="font-sans text-xs text-white mt-1 whitespace-nowrap">
                      {featured.location} &middot; Verified {featured.platform}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(featured.rating)].map((_, i) => <i key={i} className="ri-star-fill text-luxury-gold text-base" />)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial Grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {TESTIMONIALS.map((t, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`text-left p-6 rounded-2xl border transition-all duration-300 cursor-pointer group ${
                i === active
                  ? 'border-luxury-gold/40 bg-luxury-gold/8'
                  : 'border-white/8 hover:border-luxury-gold/28'
              }`}
              style={{ background: i === active ? 'rgba(26,101,224,0.07)' : 'rgba(255,255,255,0.04)', backdropFilter: 'blur(10px)' }}
            >
              <div className="flex justify-between items-center mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full border border-luxury-gold/25 bg-luxury-gold/10">
                  <span className="font-serif text-luxury-gold font-bold text-base">{t.initials}</span>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(t.rating)].map((_, j) => <i key={j} className="ri-star-fill text-luxury-gold text-xs" />)}
                </div>
              </div>
              <p className="font-sans text-xs text-white leading-relaxed mb-3 line-clamp-3">{t.quote}</p>
              <div className="font-caps text-[9px] tracking-[0.18em] uppercase text-luxury-gold whitespace-nowrap">
                {t.name} &middot; {t.location}
              </div>
            </button>
          ))}
        </div>

        {/* Post-testimonial CTA */}
        <div
          className={`text-center transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="font-serif text-white text-2xl lg:text-3xl mb-6">
            Ready to experience the <em className="not-italic text-luxury-gold">Atlantis difference?</em>
          </p>
          <a
            href="tel:9058454009"
            className="inline-flex items-center gap-3 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps font-bold text-xs tracking-[0.18em] uppercase px-10 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap shadow-gold"
          >
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-sm" /></div>
            <span>Call (905) 845-4009 Today</span>
          </a>
        </div>
      </div>
    </section>
  );
}
